<template lang="pug">
div.user-content
	template(v-if="isReported")
		v-alert.my-6(type="warning" color="warning" text border="bottom")
			.user-content__actions
				v-btn(tile x-small color="warning" v-if="adminPath" :href="adminPath" target="_blank")
					v-icon(x-small) shield
			strong {{$t('topic:report.threshold.reached.title')}}
			div {{$t('topic:report.threshold.reached.content')}}
		v-divider
	template(v-else-if="isSpam")
		v-alert.my-6(type="error" color="error" text border="bottom")
			.user-content__actions
				v-btn(tile x-small color="error" v-if="adminPath" :href="adminPath" target="_blank")
					v-icon(x-small) shield
			strong {{$t('topic:marked_as_spam.title')}}
			div {{$t('topic:marked_as_spam.content')}}
		v-divider
	v-row.user-content__row(align="start" justify="start" no-gutters v-else :id="`${id.slice(24)}`")
		v-col.shrink.hidden-sm-and-down
			slot(name="avatar")
				v-avatar.mr-3(:class="{'gradient': user.role === 'ADMIN', 'clubmember': user.isClubmember}")
					v-img(v-if="user.avatar" :src="`${userAvatar}?width=160`" v-on:error="onUserAvatarError")
					v-img(v-else src="https://static.wintersport.nl/images/avatar-w48.png")
		v-col
			client-only
				.user-content__actions
					template(v-if="votes")
						login-action(@on-click="vote")
							template(#activator="{ on }")
								v-btn(tile x-small :color="votes.voted ? 'success':'white'" :class="{'disable-events': votes && votes.voted}" v-on="on")
									v-icon(x-small :color="votes.voted ? null:'grey'") thumb_up
						v-btn.disable-events(tile x-small depressed :color="votes.total > 0 ? 'success':'white'" :class="votes.total > 0 ? null : 'grey--text'") {{votes.total}}
					v-btn(tile x-small color="white" v-if="quotable" @click="$emit('quote', {user, content, date})")
						v-icon(x-small color="grey") format_quote
					v-btn(tile x-small color="white" v-if="canEdit" @click="edit")
						v-icon(x-small color="grey") edit
					login-action(@on-click="reportDialog=true")
						template(#activator="{ on }")
							v-btn(tile x-small color="white"  v-on="on" )
								v-icon(x-small color="grey") report_problem
					v-btn(tile x-small color="white" v-if="adminPath" :href="adminPath" target="_blank")
						v-icon(x-small color="grey") shield
				.mb-2
					slot(name="user")
						magic-nuxt-link.mr-2.font-weight-bold(:to="user") {{user.username}}
						v-chip.mr-2.pa-1.gradient(v-if="user.role === 'ADMIN' && user.roleLabel && !isAuthor" label color="accent" x-small) {{user.roleLabel}}
						v-chip.mr-2.pa-1(v-else-if="isAuthor" label color="accent" x-small) {{$t('user.content.item:author.label')}}
						nuxt-link(v-else-if="user.isClubmember" :to="localePath({name:'memberships'})")
							v-icon.mr-2.ml-n1.mt-n1(color="#00C6FF" small) verified
					v-rating.d-inline.mr-2(v-if="rating" :value="rating/2" readonly small dense color="#ffb400" background-color="#ffb400")
					span.caption.grey--text.shrink.text-no-wrap.mr-2(v-if="date" :title="editedText") {{$dayjs(date).format('D MMMM YYYY · HH:mm')}}
			ws-content(:value="editableObject.content" :parserType="parserType")
			p(v-if="editedText").caption.grey--text *{{editedText}}
			v-chip.user-content__signature.grey--text(color="grey lighten-4" small label v-if="user.signature") {{user.signature}}
			div
				slot(name="chips")
		ws-dialog(v-model="reportDialog" :title="$t('report:heading')")
			report(:id="id" :type="type" v-if="reportDialog" v-on:close-dialog="reportDialog=false" v-on:reported="$toast.success($t('reply:not-ok.report:is.send.alert')); reportDialog=false")

		ws-dialog(v-model="showEditTimePassedDialog" :title="$t('reply.edit.dialog.time.passed:heading')")
			v-card-text.mt-6
				p.text--primary {{$t('reply.edit.dialog.time.passed:content')}}

		ws-dialog(v-model="showEditReplyDialog" :title="$t('reply.edit.form:heading')")
			edit-form(v-model="editableObject" v-if="showEditReplyDialog" :type="type" @input="showEditReplyDialog = false")
</template>

<script>
/* eslint-disable vue/no-mutating-props */
import gql from 'graphql-tag';
import fragments from './fragments.js';

const EditForm = () =>
	import(/* webpackChunkName: "usercontentEdit" */ './edit.vue');
const Report = () =>
	import(/* webpackChunkName: "usercontentReport" */ './report.vue');

export default {
	components: { Report, EditForm },
	inheritAttrs: false,
	props: {
		id: {
			type: String,
			required: true
		},
		type: {
			type: String,
			required: true
		},
		adminPath: {
			type: String,
			default: ''
		},
		user: {
			type: Object,
			required: true
		},
		content: {
			type: Object,
			required: true
		},
		edited: {
			type: Object,
			default() {
				return null;
			}
		},
		moderation: {
			type: Object,
			default() {
				return null;
			}
		},
		date: {
			type: String,
			default: null
		},
		rating: {
			type: Number,
			default: 0
		},
		votes: {
			type: Object,
			default() {
				return null;
			}
		},
		quotable: {
			type: Boolean,
			default: false
		},
		editable: {
			type: Boolean || Number,
			default: false
		},
		isSpam: {
			type: Boolean,
			default: false
		},
		isReported: {
			type: Boolean,
			default: false
		},
		parserType: {
			type: String,
			default: 'ALL'
		},
		isAuthor: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			reportDialog: false,
			showEditReplyDialog: false,
			showEditTimePassedDialog: false,
			editableObject: {},
			userAvatarFailed: false
		};
	},
	computed: {
		canEdit() {
			if (!this.editable) {
				return false;
			}

			if (this.moderation && this.$store.state.user.role !== 'ADMIN') {
				return false;
			}

			return this.$store.state.user.id === this.user.id;
		},
		editedText() {
			if (this.moderation) {
				const text = this.moderation.user
					? this.$t('topic:message.edited.by.{moderator}.{date}', {
							moderator: this.moderation.user.username,
							date: this.$dayjs(this.moderation.created_at).format(
								'DD MMM YYYY HH:mm'
							)
					  })
					: this.$t('topic:message.edited.by.moderator.{date}', {
							date: this.$dayjs(this.moderation.created_at).format(
								'DD MMM YYYY HH:mm'
							)
					  });
				return this.moderation.commentPublic
					? `${text}: ${this.moderation.commentPublic}`
					: text;
			}
			if (this.editableObject.edited) {
				return this.$t('reply:message.edited.by.{user}.{date}', {
					user: this.editableObject.edited.editedBy
						? this.editableObject.edited.editedBy.username
						: null,
					date: this.$dayjs(this.editableObject.edited.editedAt).format(
						'DD MMM YYYY HH:mm'
					)
				});
			}
			return '';
		},
		userAvatar() {
			return this.userAvatarFailed
				? 'https://static.wintersport.nl/images/avatar-w48.png'
				: this.user.avatar;
		}
	},
	created() {
		this.assignEditableObject();
		this.$watch(
			vm => [vm.id, vm.content, vm.edited].join(),
			function () {
				this.assignEditableObject();
			}
		);
	},
	methods: {
		assignEditableObject() {
			this.editableObject = {
				id: this.id,
				content: this.content,
				edited: this.edited
			};
		},
		edit() {
			if (this.$dayjs(Date()).diff(this.date, 'minutes') <= 30) {
				this.showEditReplyDialog = true;
			} else {
				this.showEditTimePassedDialog = true;
			}
		},
		vote() {
			if (this.$store.state.user.id) {
				const vote = {
					voteable_type: this.type,
					voteable_id: this.id
				};

				this.$graphClient
					.mutate({
						mutation: gql`
							mutation ($vote: VoteInput) {
								vote(vote: $vote) {
									total
									voted
								}
							}
						`,
						errorPolicy: 'all',
						variables: {
							vote
						}
					})
					.then(({ _data, errors }) => {
						if (errors) {
							throw errors[0];
						}

						this.votes.total++;
						this.votes.voted = true;
					})
					.catch(_error => {
						this.$toast.error(this.$t('reply:upvote.error'));
					});
			}
		},
		onUserAvatarError(_event) {
			this.userAvatarFailed = true;
		}
	},
	fragments
};
</script>

<style lang="sass">

.user-content
	&__actions
		float: right
		.v-btn
			box-shadow: none !important
			border: 1px solid var(--v-border-base) !important

			&.disable-events
				pointer-events: none
			&:not(:first-child)
				border-width: 1px 1px 1px 0px !important
			&:first-child
				border-radius: 4px 0 0 4px
			&:last-child
				border-radius: 0 4px 4px 0
				&:first-child
					border-radius: 4px

	&__signature
		.v-chip__content
			white-space: normal


	&__row
		border-bottom: 1px solid var(--v-border-base)
		padding: $spacer * 6 0

		> p
			margin: $spacer * 3 0 0
</style>
